
import { supabase } from '@/integrations/supabase/client';

/**
 * Extracts the file path from a Supabase storage URL
 * Handles various URL formats including cache-busted URLs
 */
const extractFilePathFromUrl = (url: string): { bucketName: string; filePath: string } | null => {
  // Remove cache busting parameters and fragments
  const cleanUrl = url.split('?')[0].split('#')[0];
  
  // Try multiple regex patterns to extract bucket and file path
  const patterns = [
    // Standard format: https://project.supabase.co/storage/v1/object/public/bucket/path/to/file.jpg
    /https?:\/\/[^\/]+\.supabase\.co\/storage\/v1\/object\/(?:public|sign)\/([^\/]+)\/(.+)/,
    // Alternative format without full domain
    /\/storage\/v1\/object\/(?:public|sign)\/([^\/]+)\/(.+)/,
    // Direct object path format
    /\/object\/(?:public|sign)\/([^\/]+)\/(.+)/,
  ];
  
  for (const pattern of patterns) {
    const match = cleanUrl.match(pattern);
    if (match && match[1] && match[2]) {
      return {
        bucketName: match[1],
        filePath: match[2]
      };
    }
  }
  
  // Last resort: if URL contains 'avatars' bucket, try to extract filename
  if (cleanUrl.includes('/avatars/')) {
    const parts = cleanUrl.split('/avatars/');
    if (parts[1]) {
      return {
        bucketName: 'avatars',
        filePath: parts[1]
      };
    }
  }
  
  return null;
};

export const deleteAvatarFromStorage = async (avatarUrl: string | null): Promise<boolean> => {
  if (!avatarUrl) {
    console.log('No avatar URL provided, skipping deletion');
    return false;
  }
  
  try {
    console.log('🗑️ Attempting to delete avatar:', avatarUrl);
    
    const extracted = extractFilePathFromUrl(avatarUrl);
    
    if (!extracted) {
      console.warn('⚠️ Could not extract file path from URL:', avatarUrl);
      return false;
    }
    
    const { bucketName, filePath } = extracted;
    console.log('📂 Parsed bucket:', bucketName, 'path:', filePath);
    
    // Delete the file from storage
    const { data, error } = await supabase.storage
      .from(bucketName)
      .remove([filePath]);
    
    if (error) {
      // Log the error but don't throw - the file might already be deleted
      console.error('❌ Failed to delete avatar from storage:', error.message);
      return false;
    }
    
    console.log('✅ Successfully deleted avatar from storage:', filePath, data);
    return true;
  } catch (error) {
    console.error('❌ Error in deleteAvatarFromStorage:', error);
    return false;
  }
};


export const deleteOldAvatarAndUploadNew = async (
  oldAvatarUrl: string | null,
  file: File,
  bucketName: string,
  filePath: string
): Promise<string> => {
  try {
    console.log('🔄 Starting avatar upload process');
    console.log('📁 Old avatar URL:', oldAvatarUrl);
    console.log('📁 New file path:', filePath);
    
    // Delete old avatar FIRST (before upload) to prevent orphaned files
    if (oldAvatarUrl) {
      console.log('🗑️ Deleting old avatar before uploading new one...');
      const deleted = await deleteAvatarFromStorage(oldAvatarUrl);
      if (deleted) {
        console.log('✅ Old avatar deleted successfully');
      } else {
        console.warn('⚠️ Could not delete old avatar, proceeding with upload anyway');
      }
    }
    
    // Upload new avatar
    const { data, error: uploadError } = await supabase.storage
      .from(bucketName)
      .upload(filePath, file, {
        upsert: true
      });
    
    if (uploadError) {
      console.error('❌ Upload failed:', uploadError);
      throw uploadError;
    }
    
    console.log('✅ Upload successful:', data);
    
    // Get public URL with cache busting
    const { data: { publicUrl } } = supabase.storage
      .from(bucketName)
      .getPublicUrl(filePath);
    
    // Add timestamp for cache busting
    const cacheBustedUrl = `${publicUrl}?t=${Date.now()}`;
    
    console.log('✅ Upload process completed successfully');
    console.log('🔗 New avatar URL:', cacheBustedUrl);
    
    return cacheBustedUrl;
  } catch (error) {
    console.error('❌ Error in deleteOldAvatarAndUploadNew:', error);
    throw error;
  }
};
